import * as Combobox from '@givebutter/primitives/combobox';
import { CampaignSelectFormValues } from 'assets/js/pages/engage/workflows/components/types';
import { useCampaignSelectItems } from 'assets/js/pages/engage/workflows/hooks/useCampaignSelectItems';
import * as Form from 'components/form';

const anyCampaignItem = {
    id: 'any',
    label: 'Any campaign',
};

const defaultCampaignSelectValue = [anyCampaignItem.id];

type CampaignSelectResource = 'campaigns' | 'auction-campaigns' | 'event-campaigns';

interface CampaignSelectProps {
    resource?: CampaignSelectResource;
    path?: 'campaigns' | 'value.campaigns';
}

const CampaignSelect = ({ resource = 'campaigns', path = 'campaigns' }: CampaignSelectProps) => {
    const { data: campaignsData } = useCampaignSelectItems(resource, {
        select: campaigns =>
            campaigns.map(campaign => ({
                id: campaign.id.toString(),
                label: campaign.title,
            })),
    });

    const { control, setValue } = Form.useGivebutterFormContext<CampaignSelectFormValues>();

    return (
        <Form.Container>
            <Form.Label name={path}>Campaign</Form.Label>

            <Form.Combobox
                control={control}
                name={path}
                resource="campaign"
                rules={{
                    validate: values => (values && values.length > 0) || 'Campaign is required',
                }}
            >
                <Combobox.Trigger aria-label="Select a campaign">
                    <Combobox.Select placeholder="Select a campaign" />
                </Combobox.Trigger>
                <Combobox.Popover>
                    <Combobox.Search />
                    <Combobox.Items>
                        <Combobox.Item
                            key={anyCampaignItem.id}
                            value={anyCampaignItem.id}
                            onSelectItem={({ event }) => {
                                event.preventDefault();
                                setValue(path, [anyCampaignItem.id], { shouldDirty: true });
                            }}
                        >
                            <Combobox.CheckboxIndicator />
                            <Combobox.ItemText>
                                <p className="font-medium">{getAnyItemText(resource)}</p>
                            </Combobox.ItemText>
                        </Combobox.Item>

                        {campaignsData?.map(campaign => {
                            return (
                                <Combobox.Item
                                    key={campaign.id}
                                    value={campaign.id}
                                    onSelectItem={({ event, selectedItems, selectedItem }) => {
                                        event.preventDefault();
                                        const selectAnyCampaign =
                                            selectedItem === anyCampaignItem.id ||
                                            selectedItems.length === 0;

                                        const newValue = selectAnyCampaign
                                            ? [anyCampaignItem.id]
                                            : selectedItems.filter(v => v !== anyCampaignItem.id);

                                        setValue(path, newValue, {
                                            shouldDirty: true,
                                        });
                                    }}
                                >
                                    <Combobox.CheckboxIndicator />
                                    <Combobox.ItemText>
                                        <p className="font-medium">{campaign.label}</p>
                                    </Combobox.ItemText>
                                </Combobox.Item>
                            );
                        })}
                    </Combobox.Items>
                </Combobox.Popover>
            </Form.Combobox>
            <Form.Error name={path} />
        </Form.Container>
    );
};

const getAnyItemText = (resource: CampaignSelectResource) => {
    switch (resource) {
        case 'campaigns':
            return 'Any campaign';
        case 'auction-campaigns':
            return 'Any auction campaign';
        case 'event-campaigns':
            return 'Any event campaign';
    }
};

export { CampaignSelect, anyCampaignItem, defaultCampaignSelectValue };
